/**
 * Toast Context
 * Global toast notification state management.
 */

import { createContext, useContext, useReducer, useCallback, type ReactNode } from 'react';
import { ToastContainer } from './ToastContainer';
import type { ToastType } from './Toast';

// ============================================
// Types
// ============================================

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: ToastItem[];
}

type ToastAction =
  | { type: 'ADD_TOAST'; payload: ToastItem }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_ALL' };

interface ToastContextValue {
  /** Show an error toast */
  showError: (message: string, options?: ToastOptions) => string;
  /** Show a success toast */
  showSuccess: (message: string, options?: ToastOptions) => string;
  /** Show a warning toast */
  showWarning: (message: string, options?: ToastOptions) => string;
  /** Show an info toast */
  showInfo: (message: string, options?: ToastOptions) => string;
  /** Dismiss a specific toast */
  dismiss: (id: string) => void;
  /** Clear all toasts */
  clearAll: () => void;
}

interface ToastOptions {
  /** Auto-dismiss duration in ms (0 = no auto-dismiss) */
  duration?: number;
  /** Action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

// ============================================
// Reducer
// ============================================

const MAX_TOASTS = 5;

function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'ADD_TOAST': {
      // Limit to MAX_TOASTS, removing oldest first
      const newToasts = [...state.toasts, action.payload];
      if (newToasts.length > MAX_TOASTS) {
        return { toasts: newToasts.slice(-MAX_TOASTS) };
      }
      return { toasts: newToasts };
    }
    case 'REMOVE_TOAST':
      return {
        toasts: state.toasts.filter((t) => t.id !== action.payload),
      };
    case 'CLEAR_ALL':
      return { toasts: [] };
    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

const ToastContext = createContext<ToastContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// ============================================
// Provider
// ============================================

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center';
}

let toastIdCounter = 0;

export function ToastProvider({ children, position = 'top-right' }: ToastProviderProps) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const addToast = useCallback(
    (type: ToastType, message: string, options?: ToastOptions): string => {
      const id = `toast-${++toastIdCounter}`;
      dispatch({
        type: 'ADD_TOAST',
        payload: {
          id,
          type,
          message,
          duration: options?.duration,
          action: options?.action,
        },
      });
      return id;
    },
    []
  );

  const showError = useCallback(
    (message: string, options?: ToastOptions) =>
      addToast('error', message, { duration: 8000, ...options }),
    [addToast]
  );

  const showSuccess = useCallback(
    (message: string, options?: ToastOptions) =>
      addToast('success', message, { duration: 4000, ...options }),
    [addToast]
  );

  const showWarning = useCallback(
    (message: string, options?: ToastOptions) =>
      addToast('warning', message, { duration: 6000, ...options }),
    [addToast]
  );

  const showInfo = useCallback(
    (message: string, options?: ToastOptions) =>
      addToast('info', message, { duration: 5000, ...options }),
    [addToast]
  );

  const dismiss = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const value: ToastContextValue = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    dismiss,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={state.toasts} onDismiss={dismiss} position={position} />
    </ToastContext.Provider>
  );
}
